
import Stepper from 'primevue/stepper';
import StepList from 'primevue/steplist';
import StepPanels from 'primevue/steppanels';
import StepItem from 'primevue/stepitem';
import Step from 'primevue/step';
import StepPanel from 'primevue/steppanel';

import IftaLabel from 'primevue/iftalabel';
import InputNumber from 'primevue/inputnumber';

import Card from 'primevue/card';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';

import { computed, defineComponent, onMounted, ref } from 'vue';
import { useAuthStore } from '../../stores/auth';
import { storeToRefs } from 'pinia';
import { useWebStore } from '../../stores/web';
import { useToast } from 'primevue/usetoast';
import { showVuePopup } from '../../utils/showVuePopup';
import WalletUpdate from '../../popup/WalletUpdate.vue';
import { useSession } from '../../stores/session';
import { depositWithdraw } from '../../apis/deposit_withdraw';
import QRCode from 'qrcode';

export default defineComponent({
	name: 'DepositThPay',
	components: {
		Stepper,
		StepList,
		StepPanels,
		StepItem,
		Step,
		StepPanel,
		IftaLabel,
		InputNumber,
		Card,
		Button,
		ProgressSpinner,
	},
	setup() {
		const toast = useToast();

		// auth
		const auth = useAuthStore();
		auth.hydrate();
		const { profile } = storeToRefs(auth);

		// web permission
		const web = useWebStore();
		web.hydrate();
		const { webPermission } = storeToRefs(web);

		// session
		const session = useSession();

		// stepper
		const stepper = ref<'1' | '2' | '3'>('1');

		// เปิดใช้ช่องทาง?
		const isActive = computed<boolean>(() => webPermission.value?.isDeposit?.isAskmepay ?? false);

		// บังคับอัปเดตบัญชีก่อน
		const isBankUpdate = computed(
			() => !(profile.value?.customerBankAccount && profile.value?.customerBankName)
		);

		// ฟอร์ม
		const form = ref({
			amount: 0,
			btn: {
				disabled: false,
				loading: false,
			},
		});

		// ลิมิต
		const amountLimit = ref({ min: 0, max: 0 });

		// สถานะ/ข้อมูลที่จะแสดงใน Step 2
		const bankDeposit = ref({
			data: {
				qr_link: '',
				orderId: '',
				amount: '',
				img: '',   // dataURL ของ QR + watermark
				name: '',
			},
			active: false,
			loading: false,
		});

		const isAmountInvalid = computed(() => {
			const v = Number(form.value.amount) || 0;
			if (amountLimit.value.min > 0 && v < amountLimit.value.min) return true;
			if (amountLimit.value.max > 0 && v > amountLimit.value.max) return true;
			return false;
		});

		onMounted(() => {
			handleGetAllService();
			handleGetMinMax();
		});

		// --------- helpers ----------
		const drawWatermark = (ctx: CanvasRenderingContext2D, text = 'WATERMARK') => {
			const { width, height } = ctx.canvas;
			ctx.save();
			// หมุนทแยงเพื่อให้ดูเป็นลายน้ำ
			ctx.translate(width / 2, height / 2);
			ctx.rotate((-20 * Math.PI) / 180);
			ctx.textAlign = 'center';
			ctx.textBaseline = 'middle';
			// ขนาดอิงตามความกว้างภาพ
			const fontSize = Math.floor(width / 10);
			ctx.font = `bold ${fontSize}px Arial`;
			ctx.fillStyle = 'rgba(255, 0, 0, 0.45)';
			ctx.fillText(text, 0, 0);
			ctx.restore();
		};

		// ---------- actions ----------
		const handleSubmit = async () => {
			form.value.btn.loading = true;
			try {
				if (isAmountInvalid.value) {
					throw new Error('จำนวนเงินไม่ถูกต้อง');
				}

				const payload = {
					amount: form.value.amount,
					serviceId: 100,
					redirectUrl: window.location.origin,
					bankName: 'SCB',
					type: 'BANK_TH',
					pin: '',
					serial: '',
					cardType: '',
				};

				const resp = await depositWithdraw.getQrcodeAskmepay(payload);
				if (resp.code !== '0') throw new Error(resp.message);

				// วาด QR ลง canvas แล้วซ้อนลายน้ำ
				const canvas = document.createElement('canvas');
				const size = 320; // ขนาด QR (px)
				canvas.width = size;
				canvas.height = size;

				// ข้อสำคัญ: ใช้ toCanvas (ไม่ใช่ toDataURL) เพื่อให้วาดลง canvas โดยตรง
				await QRCode.toCanvas(canvas, resp.result.img, {
					errorCorrectionLevel: 'H',
					margin: 1,
					width: size,
				});

				const ctx = canvas.getContext('2d')!;
				drawWatermark(ctx, 'สำหรับเติมเงินเว็บคาสิโนออนไลน์เท่านั้น');

				// png — QR ต้องคมและปลายทางรับแค่ jpeg/png (lossless เล็กอยู่แล้วสำหรับภาพ 2 สี)
				const dataUrl = canvas.toDataURL('image/png');

				bankDeposit.value.data = {
					qr_link: resp.result.qr_link,
					orderId: resp.result.orderId,
					amount: resp.result.amount,
					img: dataUrl,
					name: resp.result.name,
				};

				stepper.value = '2';
			} catch (error: any) {
				toast.add({
					severity: 'error',
					summary: 'เกิดข้อผิดพลาดบางอย่าง',
					detail: error?.message || String(error),
					life: 3000,
				});
			} finally {
				form.value.btn.loading = false;
			}
		};

		const handleGetAllService = async () => {
			try {
				const resp = await depositWithdraw.getAllServiceAskmepay({ type: '' });
				if (resp.code !== '0') throw new Error(resp.message);
				// console.log(resp);
			} catch (error: any) {
				toast.add({
					severity: 'error',
					summary: 'เกิดข้อผิดพลาดบางอย่าง',
					detail: error?.message || String(error),
					life: 3000,
				});
			}
		};

		const handleGetMinMax = async () => {
			try {
				const resp = await depositWithdraw.getMinMaxAskmepay({ serviceId: 100 });
				if (resp.code !== '0') throw new Error(resp.message);
				amountLimit.value.min = parseInt(resp.result.min);
				amountLimit.value.max = parseInt(resp.result.max);
			} catch (error: any) {
				toast.add({
					severity: 'error',
					summary: 'เกิดข้อผิดพลาดบางอย่าง',
					detail: error?.message || String(error),
					life: 3000,
				});
			}
		};

		const btnClickUpdateBank = async () => {
			session.set('navbar_primary', 'wallet');
			session.set('navbar_sub', 'wallet:update');
			await showVuePopup(WalletUpdate, { tabs: 'bank' });
		};

		return {
			profile,
			stepper,
			form,
			amountLimit,
			handleSubmit,
			bankDeposit,
			isActive,
			isBankUpdate,
			isAmountInvalid,
			btnClickUpdateBank,
		};
	},
});
